import { useCallback, useState } from 'react';
import { TableViewMode } from '../components/common/responsiveTable.types';
import { useIsMobile } from './useIsMobile';

export const GLOBAL_KEY = 'oms.tableView.global';
export const pageKeyFor = (pageKey: string) => `oms.tableView.${pageKey}`;

const VALID: TableViewMode[] = ['cards', 'compact', 'table'];

/** Reads a stored mode, ignoring anything we did not write (corrupt or stale values). */
const readStored = (key: string): TableViewMode | null => {
    try {
        const raw = window.localStorage.getItem(key);
        return VALID.includes(raw as TableViewMode) ? (raw as TableViewMode) : null;
    } catch {
        // Safari private mode throws on localStorage access. A missing preference is fine.
        return null;
    }
};

/**
 * Resolves how this page's table should render.
 *
 * Precedence, highest first: this page's stored override, the user's global preference, then the
 * screen-derived default. Preferences are per-browser by design — there is deliberately no server
 * round-trip, no DB column and no settings screen for this.
 */
export const useTableViewMode = (pageKey: string) => {
    const isMobile = useIsMobile();
    const [override, setOverride] = useState<TableViewMode | null>(() => readStored(pageKeyFor(pageKey)));

    const mode: TableViewMode =
        override
        ?? readStored(GLOBAL_KEY)
        ?? (isMobile ? 'cards' : 'table');

    /**
     * The FIRST mode change a user ever makes anywhere becomes their global default; every later
     * change writes only that page's override.
     *
     * That is what makes spec D4's "one global default plus per-page overrides" reachable without
     * a settings screen: picking cards once on any page gives you cards everywhere, and then
     * switching a single page to table leaves the rest on cards. Writing the page key every time
     * (as this did before) left GLOBAL_KEY permanently unwritten, so the global tier of the
     * precedence chain below was dead and a user could only ever set a preference page by page.
     */
    const setMode = useCallback((next: TableViewMode) => {
        setOverride(next);
        try {
            // Deliberately the RAW value, not readStored: a corrupt entry still means the user
            // has chosen before, so it must not be overwritten as if it were a first choice.
            const globalSet = window.localStorage.getItem(GLOBAL_KEY) !== null;
            window.localStorage.setItem(globalSet ? pageKeyFor(pageKey) : GLOBAL_KEY, next);
        } catch {
            // Preference is lost on reload but the session still works. Not worth surfacing.
        }
    }, [pageKey]);

    const resetToDefault = useCallback(() => {
        setOverride(null);
        try {
            window.localStorage.removeItem(pageKeyFor(pageKey));
        } catch {
            // As above.
        }
    }, [pageKey]);

    return { mode, setMode, resetToDefault };
};

export default useTableViewMode;
